import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import type { MyCommunity, UnreadCounts } from "@atrium/shared";
import { api } from "./api";

export function useMediaQuery(query: string) {
  return useSyncExternalStore(
    (cb) => {
      const m = window.matchMedia(query);
      m.addEventListener("change", cb);
      return () => m.removeEventListener("change", cb);
    },
    () => window.matchMedia(query).matches,
  );
}

export const useIsDesktop = () => useMediaQuery("(min-width: 768px)");
export const useIsWide = () => useMediaQuery("(min-width: 1100px)");

export function useDebounced<T>(value: T, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Fires `onVisible` when the sentinel scrolls into view (infinite scrolling). */
export function useInView<T extends Element>(onVisible: () => void, enabled = true, rootMargin = "600px") {
  const ref = useRef<T | null>(null);
  const cb = useRef(onVisible);
  cb.current = onVisible;
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;
    const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && cb.current(), { rootMargin });
    io.observe(el);
    return () => io.disconnect();
  }, [enabled, rootMargin]);
  return ref;
}

export function useUnread() {
  return useQuery({ queryKey: ["unread"], queryFn: () => api.get<UnreadCounts>("/api/unread"), staleTime: 60_000 });
}

export function useMyCommunities() {
  return useQuery({ queryKey: ["my-communities"], queryFn: () => api.get<{ items: MyCommunity[] }>("/api/me/communities").then((r) => r.items) });
}

export function useNow(intervalMs = 60_000) {
  const [, setT] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setT((x) => x + 1), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
}

export function useDocumentTitle(title: string | undefined) {
  useEffect(() => {
    if (title) document.title = `${title} · Atrium`;
  }, [title]);
}
